import React from 'react';
export type Omit<P, O> = Pick<P, Exclude<keyof P, keyof O>>;

export function computeOriginalIndex(
  maxItems: number,
  chunkIndex: number,
  indexInChunk: number
): number {
  return chunkIndex * maxItems + indexInChunk;
}

export function computeOriginalIndexAfterDrop(
  maxItems: number,
  sourceChunkIndex: number,
  destinationChunkIndex: number,
  indexInChunk: number
): number {
  return (
    destinationChunkIndex * maxItems +
    indexInChunk +
    (sourceChunkIndex < destinationChunkIndex ? -1 : 0)
  );
}

export function splitItems(
  maxItems: number,
  items: any[],
  createId: () => string
): Chunk[] {
  const slicedItems: any[][] = sliceIntoItems(maxItems, items);
  return slicedItems.map(mapToChunk(createId));
}

function sliceIntoItems(maxItems: number, items: any[]): any[][] {
  const numberOfSlices: number = Math.ceil(items.length / maxItems);
  const sliceIndexes: number[] = Array.apply(null, Array(numberOfSlices)).map(
    (_, index: number) => index
  );
  return sliceIndexes.map((index: number) =>
    items.slice(index * maxItems, index * maxItems + maxItems)
  );
}

function mapToChunk(createId: () => string) {
  return function (items: any[]): Chunk {
    return {
      id: createId(),
      items,
    };
  };
}

interface Location {
  id: string;
  index: number;
}

export interface DragAndDropResult {
  source: Location;
  destination: Location;
}

export interface Chunk {
  id: string;
  items: any[];
}

export interface Props {
  chunks: Chunk[];
  onDragEnd(result: DragAndDropResult): void;
}

export interface WithMaxItemsProps {
  items: any[];
  maxItems?: number;
  onDragEnd(sourceIndex: number, destinationIndex: number): void;
}

interface WithMaxItemsState {
  maxItems: number;
  items: any[];
  chunks: Chunk[];
}

export const withMaxItems = <_, P extends Props>(
  Component: React.ComponentType<P>,
  createId: () => string
): React.ComponentType<Omit<P, Props> & WithMaxItemsProps> => {
  return class ComponentWithMaxItems extends React.Component<
    Omit<P, Props> & WithMaxItemsProps,
    WithMaxItemsState
  > {
    constructor(props: Omit<P, Props> & WithMaxItemsProps) {
      super(props);
      const maxItems: number =
        props.maxItems && props.maxItems > 0
          ? props.maxItems
          : props.items.length;
      this.state = {
        maxItems,
        items: props.items,
        chunks: splitItems(maxItems, props.items, createId),
      };
    }

    private findChunkIndex = (id: string): number => {
      return this.state.chunks.findIndex((chunk: Chunk) => chunk.id === id);
    };

    private onDragEnd = ({ source, destination }: DragAndDropResult): void => {
      if (destination) {
        const { index: indexInSourceChunk, id: sourceChunkId } = source;
        const { index: indexInDestinationChunk, id: destinationChunkId } =
          destination;
        const sourceChunkIndex: number = this.findChunkIndex(sourceChunkId);
        const destinationChunkIndex: number =
          this.findChunkIndex(destinationChunkId);
        const sourceIndex: number = computeOriginalIndex(
          this.state.maxItems,
          sourceChunkIndex,
          indexInSourceChunk
        );
        const destinationIndex: number = computeOriginalIndexAfterDrop(
          this.state.maxItems,
          sourceChunkIndex,
          destinationChunkIndex,
          indexInDestinationChunk
        );
        this.props.onDragEnd(sourceIndex, destinationIndex);
      }
    };

    public render = () => {
      const {
        items: _items,
        maxItems: _maxItems,
        onDragEnd: _onDragEnd,
        ...rest
      } = this.props;
      return (
        <Component
          //@ts-ignore
          chunks={this.state.chunks}
          //@ts-ignore
          onDragEnd={this.onDragEnd}
          {...(rest as unknown as P)}
        />
      );
    };
  };
};
